import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, AlertIcon, Badge, Box, Button, Divider, FormControl, FormLabel, HStack,
  IconButton, Input, Link, SimpleGrid, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack } from '@chakra-ui/react';
import { FiActivity, FiArrowUpRight, FiDownload, FiPause, FiPlay, FiRefreshCw, FiSave } from 'react-icons/fi';
import { useMoralis } from 'react-moralis';
import Moralis from 'moralis-v1';
import { TRIANGLE_AUTH_STORAGE_KEY, usableTriangleAuthorization, readTriangleAuthorization,
  signTriangleAuthorization, runTriangleCloud } from './triangleOwnerAuth.mjs';
const money = n => Number.isFinite(Number(n)) ? `$${Number(n).toFixed(2)}` : 'Collecting';
const signedMoney = n => n == null ? 'Collecting' : `${Number(n) >= 0 ? '+' : '-'}$${Math.abs(Number(n)).toFixed(2)}`;
const amount = (n, symbol) => Number(n || 0).toFixed(symbol === 'USDC' ? 2 : symbol === 'WBTC' ? 8 : 6);
const stamp = n => n ? new Date(n).toLocaleString() : '-';
const color = n => Number(n) < 0 ? '#ff8a80' : '#62dca8';

function Metric({ label, value, tint }) {
  return <Box minW={0}><Text fontSize="xs" color="#aeb8bf" mb={1}>{label}</Text>
    <Text fontSize="lg" fontWeight="600" color={tint || '#edf1f3'} overflowWrap="anywhere">{value}</Text></Box>;
}

function TargetCell({ price, target, side }) {
  const p = Number(price), t = Number(target);
  if (!(p > 0 && t > 0 && Number.isFinite(p) && Number.isFinite(t))) return <Text>-</Text>;
  const distance = (side === 'buy' ? p - t : t - p) / p * 100;
  const tint = side === 'buy' ? '#62dca8' : '#ff8a80';
  const label = distance === 0 ? 'At target' : `${Math.abs(distance).toFixed(2)}% ${distance < 0 ? 'past' : 'away'}`;
  return <Box textAlign="right">
    <Text color={tint}>{money(t)}</Text>
    <Text fontSize="xs" color={distance <= 0 ? tint : '#aeb8bf'}>{label}</Text>
  </Box>;
}

function WalletSection({ bot, onChange, busy }) {
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setDraft(bot.settings || {}); }, [bot.settings, editing]);
  const a = bot.account;
  const pending = !!bot.pending;
  const configure = async () => {
    await onChange('setTriangleBotSettings', { botInstanceId: bot.botInstanceId, settings: draft });
    setEditing(false);
  };
  const set = (key, value) => { setEditing(true); setDraft(p => ({ ...p, [key]: Number(value) })); };
  return <Box as="section" py={6} borderBottom="1px solid #354149" minW={0}>
    <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={3} mb={5}>
      <Box minW={0}>
        <HStack mb={1}><Text as="h2" fontWeight="700" fontSize="xl">{bot.botInstanceId === 'primary' ? 'Primary wallet' : 'Secondary wallet'}</Text>
          <Badge colorScheme={bot.enabled ? 'green' : pending ? 'yellow' : 'gray'}>{bot.enabled ? 'RUNNING' : pending ? 'SETTLING' : 'STOPPED'}</Badge></HStack>
        <Link href={`https://arbiscan.io/address/${bot.walletAddress}`} isExternal fontSize="xs" color="#9eb6c2" overflowWrap="anywhere">{bot.walletAddress || 'Not configured'}</Link>
      </Box>
      <Button leftIcon={bot.enabled ? <FiPause /> : <FiPlay />} colorScheme={bot.enabled ? 'red' : 'green'}
        size="sm" borderRadius="4px" isLoading={busy && !bot.enabled} isDisabled={!bot.configured || (!bot.enabled && (busy || pending))}
        onClick={() => onChange('setTriangleBotEnabled', { botInstanceId: bot.botInstanceId, enabled: !bot.enabled })}>
        {bot.enabled ? 'Stop' : 'Start triangle'}
      </Button>
    </HStack>
    {bot.error && <Alert status="error" mb={4} borderRadius="4px" bg="#40272a" color="#ffdad5"><AlertIcon />{bot.error}</Alert>}
    {!a ? <Text color="#aeb8bf">No account data available.</Text> : <>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={6}>
        <Metric label="Net PnL, after confirmed gas" value={signedMoney(a.netPnlUsd)} tint={color(a.netPnlUsd)} />
        <Metric label="Wallet equity" value={money(a.equityUsd)} />
        <Metric label="Realized / flow-adjusted" value={signedMoney(a.realizedPnlUsd)} tint={color(a.realizedPnlUsd)} />
        <Metric label="Unrealized" value={signedMoney(a.unrealizedPnlUsd)} tint={color(a.unrealizedPnlUsd)} />
        <Metric label="Estimated liquidation PnL" value={signedMoney(a.liquidationPnlUsd)} tint={color(a.liquidationPnlUsd)} />
        <Metric label="Swaps / rotations" value={`${a.tradeCount} / ${a.rotationCount}`} />
        <Metric label="Maximum drawdown" value={money(a.maxDrawdownUsd)} tint="#f4c66d" />
        <Metric label="Exposure / confirmed gas" value={`${(a.exposurePct * 100).toFixed(1)}% / ${money(a.gasPaidUsd)}`} />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={5}>
        {['day', 'week', 'month', 'year'].map(period => <Metric key={period} label={`Trailing ${period}`}
          value={signedMoney(bot.periods?.[period])} tint={bot.periods?.[period] == null ? '#aeb8bf' : color(bot.periods[period])} />)}
      </SimpleGrid>
      <Box overflowX="auto" mb={5}>
        <Table size="sm" variant="simple" sx={{ 'th, td': { borderColor: '#354149', whiteSpace: 'nowrap', px: 2, py: 3 } }}>
          <Thead><Tr><Th color="#aeb8bf">Asset</Th><Th color="#aeb8bf" isNumeric>Balance</Th><Th color="#aeb8bf" isNumeric>Pool price</Th><Th color="#aeb8bf" isNumeric>Buy below</Th><Th color="#aeb8bf" isNumeric>Sell above</Th></Tr></Thead>
          <Tbody>{['USDC', 'WETH', 'WBTC'].map(asset => <Tr key={asset}>
            <Td fontWeight="600">{asset}</Td><Td isNumeric>{amount(a.inventory?.[asset], asset)}</Td>
            <Td isNumeric>{money(bot.snapshot?.prices?.[asset])}</Td>
            <Td isNumeric>{asset === 'USDC' ? '-' : <TargetCell price={bot.snapshot?.prices?.[asset]} target={bot.targets?.[asset]?.buy} side="buy" />}</Td>
            <Td isNumeric>{asset === 'USDC' ? '-' : <TargetCell price={bot.snapshot?.prices?.[asset]} target={bot.targets?.[asset]?.sell} side="sell" />}</Td>
          </Tr>)}</Tbody>
        </Table>
      </Box>
      <HStack flexWrap="wrap" gap={2} mb={3}>
        <Badge colorScheme="cyan">FULL TRIANGLE</Badge><Badge>{bot.regime}</Badge>
        <Badge>Arbitrum / 0.05% pools</Badge><Badge>Entry {money(bot.entryUsd)}</Badge>
        <Badge>Gas model {money(bot.gasAllowanceUsd)}</Badge>
      </HStack>
      <Text fontSize="sm" color="#c9d4da" mb={2}>{bot.decision}</Text>
      {pending && <Link isExternal href={`https://arbiscan.io/tx/${bot.pending.txHash}`} color="#f4c66d" fontSize="sm">{bot.pending.phase}: {bot.pending.direction || 'Approval'} <FiArrowUpRight style={{ display: 'inline' }} /></Link>}
      <HStack color="#aeb8bf" fontSize="xs" justify="space-between" flexWrap="wrap" gap={2}>
        <Text>Block {bot.snapshot?.blockNumber} / {stamp(bot.snapshot?.timestamp)}</Text>
        <Text>Net external flows {signedMoney(a.netFlowsUsd)}</Text>
      </HStack>
      <Divider my={5} borderColor="#354149" />
      <Text fontWeight="600" mb={3}>Execution settings</Text>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        {[
          ['entryPct', 'Entry % of equity', 100, 5, 35, 1],
          ['entryMinUsd', 'Entry sizing floor (USDC)', 1, 0, 1000, 5],
          ['entryCapUsd', 'Entry cap (USDC)', 1, 10, 1000, 5],
          ['maxExecutionUsd', 'Maximum conversion (USDC)', 1, 10, 1000, 5],
          ['cashReservePct', 'USDC reserve %', 100, 10, 80, 1],
          ['minRotationAdvantageBps', 'Rotation margin (bps)', 1, 5, 100, 1],
          ['slippageBps', 'Maximum slippage (bps)', 1, 1, 50, 1],
          ['gasUsd', 'Gas budget per transaction', 1, 0.01, 2, 0.01],
          ['btcGridMinBps', 'WBTC minimum spacing (bps)', 1, 30, 300, 1],
        ].map(([key, label, scale, min, max, step]) => <FormControl key={key} minW={0}>
          <FormLabel fontSize="xs" lineHeight="16px" minH="32px" color="#aeb8bf">{label}</FormLabel>
          <Input type="number" min={min} max={max} step={step} value={draft[key] == null ? '' : Number((draft[key] * scale).toFixed(4))}
            size="sm" borderRadius="4px" borderColor="#4c5e68" isDisabled={busy || bot.enabled || pending}
            onChange={e => set(key, e.target.value / scale)} />
        </FormControl>)}
      </SimpleGrid>
      <HStack mt={4} justify="space-between" flexWrap="wrap" gap={3}>
        <Button leftIcon={<FiSave />} size="sm" borderRadius="4px" onClick={configure} isDisabled={!editing || bot.enabled || busy || pending}>Apply settings</Button>
        <Text fontSize="xs" color="#aeb8bf">Per-direction cooldown {(bot.settings.cooldownMs / 60000).toFixed(0)}m / maximum lots {bot.settings.maxLots}</Text>
      </HStack>
      <Box overflowX="auto" mt={5}>
        <Text fontWeight="600" mb={3}>Confirmed fills</Text>
        {!a.actions?.length ? <Text fontSize="sm" color="#aeb8bf">No data available.</Text> : <Table size="sm" sx={{ 'th, td': { borderColor: '#354149', whiteSpace: 'nowrap', px: 2 } }}>
          <Thead><Tr><Th color="#aeb8bf">Time</Th><Th color="#aeb8bf">Conversion</Th><Th color="#aeb8bf" isNumeric>Sent</Th><Th color="#aeb8bf" isNumeric>Received</Th><Th color="#aeb8bf">Route</Th><Th color="#aeb8bf">Transaction</Th></Tr></Thead>
          <Tbody>{[...a.actions].reverse().slice(0, 20).map(fill => <Tr key={fill.txHash}>
            <Td>{stamp(fill.timestamp)}</Td><Td>{fill.from} &gt; {fill.to}</Td><Td isNumeric>{amount(fill.amountIn, fill.from)}</Td>
            <Td isNumeric>{amount(fill.amountOut, fill.to)}</Td><Td>{fill.route}</Td>
            <Td><Link isExternal href={`https://arbiscan.io/tx/${fill.txHash}`} color="#89cee4">{fill.txHash.slice(0, 10)} <FiArrowUpRight style={{ display: 'inline' }} /></Link></Td>
          </Tr>)}</Tbody>
        </Table>}
      </Box>
    </>}
  </Box>;
}

export default function SniperBotTriangle() {
  const { account, enableWeb3 } = useMoralis();
  const [authorization, setAuthorization] = useState(() => {
    try { return readTriangleAuthorization(window.sessionStorage); } catch { return null; }
  });
  const [authorizing, setAuthorizing] = useState(false);
  const [authStage, setAuthStage] = useState('');
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const mounted = useRef(true);
  const fetchLock = useRef(false);
  const isOwner = usableTriangleAuthorization(authorization, account);
  const load = useCallback(async () => {
    if (!isOwner || fetchLock.current) return;
    fetchLock.current = true;
    setLoading(true);
    try {
      const next = await runTriangleCloud(Moralis, 'getTriangleBots', { triangleAuthorization: authorization });
      if (mounted.current) { setPayload(next); setError(''); }
    } catch (e) { if (mounted.current) setError(e.message || 'Triangle backend is unavailable'); }
    finally { fetchLock.current = false; if (mounted.current) setLoading(false); }
  }, [isOwner, authorization]);
  useEffect(() => {
    mounted.current = true;
    setPayload(null);
    if (!isOwner) return () => { mounted.current = false; };
    setError('');
    void load();
    const timer = setInterval(() => { void load(); }, 15000);
    return () => { clearInterval(timer); mounted.current = false; };
  }, [isOwner, load]);
  useEffect(() => {
    if (!authorization) return undefined;
    const clear = () => {
      setAuthorization(null);
      try { window.sessionStorage.removeItem(TRIANGLE_AUTH_STORAGE_KEY); } catch { /* Memory-only sessions still work. */ }
    };
    if (!usableTriangleAuthorization(authorization, account)) { clear(); return undefined; }
    const timer = setTimeout(clear, Math.max(0, authorization.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [authorization, account]);
  const authorize = async () => {
    if (authorizing) return;
    setAuthorizing(true);
    setError('');
    try {
      setAuthStage('Connecting wallet');
      let signer = Moralis.getInternalWeb3Provider()?.signer;
      if (!signer) {
        await enableWeb3({ provider: 'metamask', throwOnError: true });
        signer = Moralis.getInternalWeb3Provider()?.signer;
      }
      setAuthStage('Confirm owner signature');
      const proof = await signTriangleAuthorization({ signer,
        getChallenge: () => runTriangleCloud(Moralis, 'getTriangleOwnerChallenge') });
      setAuthStage('Verifying authorization');
      const next = await runTriangleCloud(Moralis, 'getTriangleBots', { triangleAuthorization: proof });
      if (!mounted.current) return;
      setAuthorization(proof);
      setPayload(next);
      try { window.sessionStorage.setItem(TRIANGLE_AUTH_STORAGE_KEY, JSON.stringify(proof)); } catch { /* Keep authorization in memory. */ }
    } catch (e) { if (mounted.current) setError(e.message || 'Owner authorization failed'); }
    finally { if (mounted.current) { setAuthorizing(false); setAuthStage(''); } }
  };
  const change = async (fn, params) => {
    const id = params.botInstanceId;
    setBusy(p => ({ ...p, [id]: true }));
    try {
      const bot = await runTriangleCloud(Moralis, fn, { ...params, triangleAuthorization: authorization });
      if (mounted.current) {
        setPayload(p => ({ ...p, bots: (p?.bots || []).map(b => b.botInstanceId === id ? bot : b) }));
        setError('');
      }
    } catch (e) { if (mounted.current) setError(e.message || 'Triangle control failed'); }
    finally { if (mounted.current) setBusy(p => ({ ...p, [id]: false })); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a'); link.href = url;
    link.download = `triangle-live-${new Date().toISOString().slice(0, 10)}.json`;
    link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const bots = payload?.bots || [];
  const combinedPnl = bots.reduce((n, b) => n + (b.account?.netPnlUsd || 0), 0);
  return <Box width="100%" maxW="1200px" bg="#151c20" color="#edf1f3" px={{ base: 3, md: 7 }} py={6} borderRadius="4px" letterSpacing="0">
    <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4} mb={6}>
      <Box><HStack><FiActivity size={24} color="#62dca8" /><Text as="h1" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="700">Full Triangle</Text></HStack>
        <Text fontSize="sm" color="#aeb8bf" mt={1}>USDC / WETH / WBTC</Text></Box>
      <HStack>
        <Tooltip label="Refresh wallet status"><IconButton aria-label="Refresh wallet status" icon={<FiRefreshCw />} onClick={load} isLoading={loading} isDisabled={!isOwner} size="sm" /></Tooltip>
        <Tooltip label="Export live report"><IconButton aria-label="Export live report" icon={<FiDownload />} onClick={download} isDisabled={!payload} size="sm" /></Tooltip>
      </HStack>
    </HStack>
    {error && <Alert status="error" mb={5} bg="#40272a" color="#ffdad5" borderRadius="4px"><AlertIcon />{error}</Alert>}
    {!isOwner ? <VStack align="stretch" py={6} spacing={4}>
      <Alert status="info" bg="#20343e" borderRadius="4px"><AlertIcon />Connect and authenticate the primary owner wallet.</Alert>
      <Button alignSelf="flex-start" colorScheme="cyan" isLoading={authorizing} loadingText={authStage}
        onClick={authorize}>Authenticate owner</Button>
    </VStack> : <>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} pb={5} borderBottom="1px solid #354149">
        <Metric label="Combined net PnL" value={signedMoney(combinedPnl)} tint={color(combinedPnl)} />
        <Metric label="Running wallets" value={`${bots.filter(b => b.enabled).length} / 2`} />
        <Metric label="Conversions" value="6 directions" />
        <Metric label="Checkpoint storage" value="2 bounded documents" />
      </SimpleGrid>
      {!bots.length && <Text py={8} color="#aeb8bf">{loading ? 'Loading wallet accounts...' : 'No data available.'}</Text>}
      {bots.map(bot => <WalletSection key={bot.botInstanceId} bot={bot} onChange={change} busy={!!busy[bot.botInstanceId]} />)}
    </>}
    <HStack justify="space-between" flexWrap="wrap" gap={3} mt={5} fontSize="xs" color="#aeb8bf">
      <Link href="/dualbot/" color="#89cee4">Paper comparison</Link>
      <Link href="/legacy-sniper" color="#89cee4">Legacy bot</Link>
      <Text>Full-triangle-1 / backend execution / refresh 15s</Text>
    </HStack>
  </Box>;
}
